import { get, set, useFormContext } from 'react-hook-form';
import { ApplicationData, ApplicationDataStore } from './ApplicationDataStore';
import { useEffect, useCallback, memo } from 'react';

interface SaveFormDataToStoreProps {
    fieldPrefix: keyof ApplicationData;
    defaultValues?:
        | ApplicationData['applicant']
        | ApplicationData['coApplicant']
        | ApplicationData['serviceProperty']
        | ApplicationData['mailingAddress']
        | ApplicationData['externalApplication'];
}

export const SaveFormDataToStore = memo(
    ({ fieldPrefix, defaultValues }: SaveFormDataToStoreProps) => {
        const { setApplicationData } = ApplicationDataStore.useStore();
        const { watch } = useFormContext();

        // First save any default values to the application data store
        useEffect(() => {
            if (defaultValues) {
                setApplicationData(prev => {
                    set(prev, fieldPrefix, defaultValues);
                    return { ...prev };
                });
            }
            // Omitting defaultValues from dependencies because including it causes infinite re-renders
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [setApplicationData, fieldPrefix]);

        // Track the form values debounced and update the application data store
        // Memoize the update function to prevent unnecessary re-renders
        const updateApplicationData = useCallback(
            (fieldValue: unknown, fieldName: string) => {
                setApplicationData(current => {
                    set(current, `${String(fieldPrefix)}.${fieldName}`, fieldValue);
                    return { ...current };
                });
            },
            [setApplicationData, fieldPrefix],
        );

        // Track the form values with debounced updates
        useEffect(() => {
            const subscription = watch((value, { name }) => {
                if (name === undefined) {
                    return;
                }
                const fieldValue = get(value, name);
                updateApplicationData(fieldValue, name);
            });

            return () => {
                subscription.unsubscribe();
            };
        }, [watch, updateApplicationData]);

        return null;
    },
);
